/**
 * Distance helpers for the event list. Coordinates are {lat, lng} in decimal degrees.
 */

const EARTH_RADIUS_KM = 6371;

/** Great-circle distance in km (haversine). */
export function distanceKm(from, to) {
    const dLat = degToRad(to.lat - from.lat);
    const dLng = degToRad(to.lng - from.lng);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(degToRad(from.lat)) * Math.cos(degToRad(to.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Parses one coordinate given as decimal ("52.52"), degrees + decimal minutes ("N52 31.12446",
 * "52 31.12446 N") or degrees/minutes/seconds ("52 31 7 N"). South and west become negative.
 * Returns NaN for anything else.
 */
export function parseCoordinate(input) {
    if (input === null || input === undefined) {
        return NaN;
    }
    let text = String(input).trim().toUpperCase();
    if (text === '') {
        return NaN;
    }
    let sign = 1;
    const hemisphere = text.match(/^([NSEW])|([NSEW])$/);
    if (hemisphere) {
        const letter = hemisphere[1] || hemisphere[2];
        sign = letter === 'S' || letter === 'W' ? -1 : 1;
        text = text.replace(/^[NSEW]|[NSEW]$/g, '').trim();
    }
    const parts = text.split(/[\s°'"]+/).filter((part) => part !== '');
    if (parts.length === 0 || parts.length > 3 || parts.some((part) => !/^-?\d+(\.\d+)?$/.test(part))) {
        return NaN;
    }
    const numbers = parts.map(Number);
    if (numbers[0] < 0) {
        sign = -sign;
        numbers[0] = -numbers[0];
    }
    const [degrees, minutes = 0, seconds = 0] = numbers;
    return sign * (degrees + minutes / 60 + seconds / 3600);
}

/** Event coordinates from map_lat/map_lng, falling back to the start coordinates evstartlat/evstartlon; null if unknown. */
export function eventCoords(event) {
    if (!event) {
        return null;
    }
    const candidates = [
        [event.map_lat, event.map_lng],
        [event.evstartlat, event.evstartlon],
    ];
    for (const [latInput, lngInput] of candidates) {
        const lat = parseCoordinate(latInput);
        const lng = parseCoordinate(lngInput);
        if (isFinite(lat) && isFinite(lng) && !(lat === 0 && lng === 0)) {
            return { lat, lng };
        }
    }
    return null;
}

/** Sort key factory: km from position, Infinity without position or event coordinates. */
export function distanceOf(position) {
    return (event) => {
        const coords = eventCoords(event);
        if (!position || !coords) {
            return Infinity;
        }
        return distanceKm(position, coords);
    };
}

function degToRad(degrees) {
    return degrees * (Math.PI / 180);
}
